import type { Offer, OfferLine } from "@/hooks/use-offers-queries";
import type { OfferSection } from "@/hooks/use-offer-sections";

/**
 * VIB-2088 (slice): duplisér et tilbud som et nytt utkast.
 * Alternative A/B-budsjetter i samme tilbud er en senere spec — denne
 * kopierer til en egen `offers`-rad så pipeline/GEEK teller hvert tilbud
 * for seg (dagens manuelle workaround, uten å lage alt på nytt).
 *
 * Ny kopi er en ny kontrakt: `include_travel_in_overhead` settes ikke,
 * så DB-default `true` (ny SOT) gjelder. Status/prosjekt/sendt-felter
 * nullstilles.
 */

export function duplicateOfferTitle(title: string): string {
  const trimmed = title.trim() || "Tilbud";
  return trimmed.startsWith("Kopi av ") ? `${trimmed} (2)` : `Kopi av ${trimmed}`;
}

export type DuplicatedOfferInsert = {
  title: string;
  customer_id: string | null;
  customer_name: string | null;
  description: string | null;
  created_by: string;
  status: "draft";
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  startup_invoice_pct: number;
  final_invoice_pct: number;
  total_amount: number;
  hourly_rate: number | null;
  show_invoice_terms: boolean;
  invoice_terms: Offer["invoice_terms"];
  custom_terms_text: string | null;
  concept_routing: boolean;
  writers: Offer["writers"];
  bills_travel_per_medgatt: boolean;
  source: "manual";
};

export function buildDuplicatedOfferInsert(
  source: Offer,
  createdBy: string,
): DuplicatedOfferInsert {
  return {
    title: duplicateOfferTitle(source.title),
    customer_id: source.customer_id,
    customer_name: source.customer_name,
    description: source.description,
    created_by: createdBy,
    status: "draft",
    overhead_pct: source.overhead_pct,
    discount_pct: source.discount_pct,
    discount_amount: source.discount_amount,
    startup_invoice_pct: source.startup_invoice_pct,
    final_invoice_pct: source.final_invoice_pct,
    total_amount: source.total_amount,
    hourly_rate: source.hourly_rate,
    show_invoice_terms: source.show_invoice_terms,
    invoice_terms: source.invoice_terms,
    custom_terms_text: source.custom_terms_text,
    concept_routing: source.concept_routing,
    writers: source.writers,
    bills_travel_per_medgatt: source.bills_travel_per_medgatt,
    source: "manual",
  };
}

export function buildDuplicatedSectionInserts(
  sections: OfferSection[],
  newOfferId: string,
): Array<{
  name: string;
  section_type: OfferSection["section_type"];
  sort_order: number;
  custom_terms_text: string | null;
  offer_id: string;
}> {
  return sections.map((s) => ({
    offer_id: newOfferId,
    name: s.name,
    section_type: s.section_type,
    sort_order: s.sort_order,
    custom_terms_text: s.custom_terms_text,
  }));
}

export type DuplicatedLineInsert = {
  offer_id: string;
  template_id: string | null;
  phase: OfferLine["phase"];
  description: string;
  cost_type: string;
  pricing_type: OfferLine["pricing_type"];
  quantity: number;
  unit: string;
  unit_price: number;
  purchase_price: number;
  amount: number;
  comment: string | null;
  sort_order: number;
  is_auto_generated: boolean;
  auto_kind: string | null;
  overtime_split: OfferLine["overtime_split"];
  section_id: string | null;
  rate_basis_hourly: number | null;
};

export function buildDuplicatedLineInserts(
  lines: OfferLine[],
  newOfferId: string,
  sectionIdMap: Map<string, string>,
): DuplicatedLineInsert[] {
  return lines.map((line) => ({
    offer_id: newOfferId,
    template_id: line.template_id,
    phase: line.phase,
    description: line.description,
    cost_type: line.cost_type,
    pricing_type: line.pricing_type,
    quantity: line.quantity,
    unit: line.unit,
    unit_price: line.unit_price,
    purchase_price: line.purchase_price,
    amount: line.amount,
    comment: line.comment,
    sort_order: line.sort_order,
    is_auto_generated: line.is_auto_generated ?? false,
    auto_kind: line.auto_kind ?? null,
    overtime_split: line.overtime_split ?? null,
    section_id: line.section_id
      ? (sectionIdMap.get(line.section_id) ?? null)
      : null,
    rate_basis_hourly: line.rate_basis_hourly ?? null,
  }));
}
